import {ownerDecision} from './releaseOwnerDecision.ts'
import type { ReleasePr } from "../api/releases";
import { workflowBlockerText } from "./workflowOwner.ts";

export function workflowReceipt(pr: ReleasePr) {
  const w = pr.workflow;
  return w &&
      /^[a-f0-9-]{36}$/i.test(w.approval_id) &&
      w.approved_head_sha === pr.head_sha &&
      w.task_id === pr.task?.id &&
      ["night", "emergency"].includes(w.mode)
    ? w
    : null;
}

const workflowHeld = (w: NonNullable<ReleasePr["workflow"]>) =>
  ["held", "failed", "uncertain"].includes(w.status) ||
  /mirror|capabil|protocol/.test(w.reason ?? "");
export function exactApproval(pr: ReleasePr) {
  const workflow = workflowReceipt(pr);
  if (workflow) {
    return (
      workflow.mode === "night" &&
      ["waiting", "dispatching"].includes(workflow.status) &&
      !workflowHeld(workflow)
    );
  }
  return (
    /^[a-f0-9]{40}$/i.test(pr.head_sha) &&
    pr.approved_sha === pr.head_sha &&
    pr.approved_by?.toLowerCase() === "nizan@otomator.co.il" &&
    pr.approved_mode === "night" &&
    Boolean(pr.approved_at) &&
    Number.isFinite(Date.parse(pr.approved_at!))
  );
}
export function releaseBucket(
  pr: ReleasePr,
): "tonight" | "waiting" | "stuck" | "agents" {
  if (
    pr.draft ||
    ["in_progress", "in-progress", "doing", "active"].includes(
      pr.task?.status ?? pr.control_snapshot?.task_status ?? "",
    )
  ) {
    return "agents";
  }
  const blocker = pr.workflow_blocker?.code==='draft'&&!pr.draft?null:pr.workflow_blocker;
  if (pr.task_link_state === "pending") return "agents";
  const workflow = workflowReceipt(pr);
  if (workflow && workflowHeld(workflow)) return "stuck";
  const recorded=ownerDecision(pr);if(recorded)return recorded.mode==='night'?'tonight':'agents';
  if (
    workflow?.mode === "emergency" &&
    ["waiting", "dispatching"].includes(workflow.status) &&
    !blocker
  ) {
    return "agents";
  }
  if (exactApproval(pr) && (!blocker || blocker.code === "owner_approval")) {
    return "tonight";
  }
  if (
    blocker && !["owner_approval", "screens_approval"].includes(blocker.code) &&
    !(blocker.code === "task_link_missing" && pr.task_link_state === "verified")
  ) {
    return "stuck";
  }
  if (blocker?.owner === "nizan" && pr.task) return "waiting";
  if (exactApproval(pr) && !blocker) return "tonight";
  if (
    pr.task && ["CLEAN", "HAS_HOOKS"].includes(pr.merge_state) &&
    /ממתין לאישור (ניצן|ניזן|בעלים)/.test(pr.blocker ?? "")
  ) return "waiting";
  return pr.task ? "agents" : "stuck";
}
export function releaseGroups(prs: ReleasePr[]) {
  const groups = new Map<string, ReleasePr[]>();
  for (const pr of prs.filter((p) => p.state === "open")) {
    const key = pr.task?.id ?? pr.url;
    groups.set(key, [...(groups.get(key) ?? []), pr]);
  }
  return [...groups].map((
    [id, rows],
  ): {
    id: string;
    prs: ReleasePr[];
    title: string;
    bucket: ReturnType<typeof releaseBucket>;
  } => ({
    id,
    prs: rows,
    title: rows[0].task?.title ?? rows[0].title,
    bucket: rows.some((p) => releaseBucket(p) === "agents")
      ? "agents"
      : rows.some((p) => releaseBucket(p) === "stuck")
      ? "stuck"
      : rows.every((p) => releaseBucket(p) === "tonight")
      ? "tonight"
      : "waiting",
  }));
}
export function releaseReason(pr: ReleasePr) {
  if (pr.state === "merged") {
    return "ה־PR כבר מוזג · אפשר לעיין בשינויים, ללא פעולת שחרור נוספת.";
  }
  if (pr.state === "closed") {
    return "ה־PR נסגר ללא מיזוג · פעולות שחרור וסגירה אינן זמינות.";
  }
  if (pr.identity_only) {
    return "נתוני היסטוריה בלבד · הבדיקות ומוכנות השחרור טרם אומתו.";
  }
  if (!pr.task && pr.task_link_state === "unknown") {
    return "מצב קישור המשימה אינו זמין · נדרש רענון";
  }
  if (
    pr.draft
  ) return "טיוטה · הסוכן משלים את העבודה והבדיקות";
  if (pr.task_link_state === "pending") {
    return "המשימה זוהתה · הסוכן מאמת את הקישור לגרסה הנוכחית";
  }
  const decision=ownerDecision(pr)
  if(decision)return decision.mode==='night'?'אושר ללילה · ימוזג ב־23:15':'אושר למיזוג עכשיו · ממזג אחרי שהבדיקות עוברות'
  const workflow = workflowReceipt(pr);
  if (workflow) {
    if (workflowHeld(workflow)) {
      return `נרשם אישור, הביצוע ${
        workflow.status === "uncertain" ? "דורש בירור" : "מוחזק"
      } · הסוכן: ${
        workflowBlockerText(workflow.reason ?? "executor_unavailable")
      }`;
    }
    if (
      ["waiting", "dispatching"].includes(workflow.status) &&
      (!pr.workflow_blocker || pr.workflow_blocker.code === "owner_approval")
    ) {
      return workflow.mode === "night"
        ? "נרשם אישור לחבילת המשימה בגרסאות המדויקות · בתור הלילה; המיזוג והפריסה טרם הושלמו"
        : "נרשם אישור חירום לחבילה · ממתין לביצוע ולאימות";
    }
  }
  if (
    pr.workflow_blocker && !(pr.workflow_blocker.code==='draft'&&!pr.draft) &&
    !(pr.workflow_blocker.code === "task_link_missing" &&
      pr.task_link_state === "verified")
  ) {
    return `${pr.workflow_blocker.text} · ${
      pr.workflow_blocker.owner === "nizan" ? "ניצן" : "הסוכן"
    }: ${pr.workflow_blocker.next_step}`;
  }
  if (pr.draft) return "טיוטה · הסוכן משלים את העבודה והבדיקות";
  if (!pr.task) {
    return pr.task_link_state === "unknown"
      ? "מצב קישור המשימה אינו זמין · נדרש רענון"
      : "לא מקושר למשימה · לא ייצא בלי קישור · הסוכן מטפל";
  }
  if (
    pr.blocker?.trim() && !(!pr.draft&&/טיוטה/.test(pr.blocker)) &&
    !(pr.task_link_state === "verified" &&
      /לא מקושר|חסר קישור|task_link/.test(pr.blocker))
  ) {
    return pr.blocker +
      (/ממתין לאישור (ניצן|ניזן|בעלים)/.test(pr.blocker)
        ? " · ניצן: לבדוק ולאשר את הגרסה המדויקת"
        : " · הסוכן מאמת את החסם");
  }
  if (exactApproval(pr)) {
    return "נרשם אישור לגרסה הנוכחית · הבקר יאמת את שערי השחרור";
  }
  return "ממתין לאימות מוכנות השחרור · הסוכן בודק את הגרסה הנוכחית";
}

// Use source prose only. English titles and technical headings are not a
// Hebrew explanation; when there is no usable sentence, omit the line.
export function whatChanges(pr: ReleasePr): string | null {
  const bodyLines = (pr.pr_body ?? "").split(/\r?\n/);
  const candidates = [pr.task?.description, pr.task?.title, ...bodyLines];
  for (const candidate of candidates) {
    for (const raw of (candidate ?? "").split(/\r?\n/)) {
      const text = raw.replace(/^\s*(?:[-*+]\s+|\d+[.)]\s+)/, "").replace(
        /\*\*|`/g,
        "",
      ).trim();
      if (
        !/[א-ת]/.test(text) || text.length < 12 ||
        /^[#<]|^Task:|^Release:|^https?:|^\|/.test(text)
      ) continue;
      if (/^(?:סיכום|בדיקות|Summary|Validation)\s*[:：]?$/.test(text)) continue;
      return /[.!?׃]$/.test(text) ? text : text + ".";
    }
  }
  return null;
}

export function releaseStatus(
  pr: ReleasePr,
  bucket?: "waiting" | "tonight" | "agents" | "stuck" | "old",
) {
  if (pr.state === "merged") return "מוזג";
  if (pr.state === "closed") return "נסגר";
  if(ownerDecision(pr))return ownerDecision(pr)!.mode==='night'?'אושר ללילה':'אושר למיזוג עכשיו';
  if (bucket === "old") return "ישן";
  if (!bucket && pr.identity_only) return "לא אומת";
  const draft = pr.draft;
  const group = bucket ?? releaseBucket(pr);
  if (group === "agents") return draft ? "טיוטה" : "בטיפול";
  return group === "waiting"
    ? "לאישור"
    : group === "tonight"
    ? "ללילה"
    : "תקוע";
}

export function nightReleaseState(
  control:
    | { config?: Record<string, unknown>; pause_until?: string | null }
    | null,
  snapshot: { repos: { repo: string; paused: boolean }[] } | null,
  now = Date.now(),
): "on" | "off" | "paused" | "unknown" {
  if (control?.config?.enabled === false || control?.config?.merges === false) {
    return "off";
  }
  if (control?.pause_until && Date.parse(control.pause_until) > now) {
    return "paused";
  }
  const global = snapshot?.repos.find((repo) => repo.repo === "*"),
    repos = snapshot?.repos.filter((repo) => repo.repo !== "*") ?? [];
  if (
    global?.paused === true ||
    repos.length > 0 && repos.every((repo) => repo.paused === true)
  ) return "off";
  if (
    control?.config?.enabled === true && control.config.merges === true &&
    global?.paused === false && repos.some((repo) => repo.paused === false)
  ) return "on";
  return "unknown";
}

export function releaseChecks(pr: ReleasePr): "passed" | "failed" | "unknown" {
  if (pr.identity_only) return "unknown";
  const snapshot = pr.control_snapshot?.head_sha === pr.head_sha
    ? pr.control_snapshot
    : null;
  if (
    pr.workflow_blocker?.code === "checks_failing" ||
    snapshot?.failing_checks === true ||
    [snapshot?.managed_check, snapshot?.managed_security].some((check) =>
      check?.head_sha === pr.head_sha && check.state === "failed"
    )
  ) return "failed";
  return snapshot?.checks_green === true ? "passed" : "unknown";
}
